import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';

const FEATURES = ['Chat', 'AI Writer', 'Rendering Helper'];
const fmt = (n) => (n || 0).toLocaleString();
const sel = 'h-9 border border-neutral-300 bg-white px-2 text-sm';

const Stat = ({ label, value, id }) => (
  <div className="bg-white border border-neutral-200 rounded-lg px-4 py-3"><p className="text-xs text-neutral-500">{label}</p><p className="text-xl font-semibold text-neutral-900" data-testid={`ai-usage-total-${id}`}>{value}</p></div>
);

const How = ({ r }) => {
  if (r.status === 'failed') return <span className="text-red-700">All models failed</span>;
  if (r.fallback_used) return <span className="text-orange-700" title={`Failed: ${(r.failed_models || []).join(', ')}`}>Fallback</span>;
  if (r.quality_requested) return <span className="text-amber-800">Quality model chosen</span>;
  return <span className="text-neutral-500">Normal</span>;
};

const Row = ({ r }) => (
  <tr className="border-t border-neutral-100" data-testid={`ai-usage-row-${r.id}`}>
    <td className="px-3 py-2 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
    <td className="px-3 py-2">{r.feature}{r.action && <span className="block text-xs text-neutral-400">{r.action}</span>}</td>
    <td className="px-3 py-2">{r.selected_label}</td>
    <td className="px-3 py-2">{r.used_label || '—'}</td>
    <td className="px-3 py-2"><How r={r} /></td>
    <td className="px-3 py-2"><span className={r.status === 'success' ? 'text-green-700' : 'text-red-700'}>{r.status === 'success' ? 'Success' : 'Failed'}</span></td>
    <td className="px-3 py-2 text-right tabular-nums" title={`In ${fmt(r.input_tokens)} · Out ${fmt(r.output_tokens)}`}>{fmt(r.total_tokens)}</td>
  </tr>
);

// Admin-only log of AI requests (no prompt or conversation text is stored).
export const AiUsageLog = ({ api }) => {
  const [feature, setFeature] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.aiUsage({ feature: feature || undefined, status: status || undefined, limit: 100 }).then(setData).catch(() => { toast.error('Could not load AI usage log'); setData({ entries: [], totals: {} }); });
  }, [api, feature, status]);
  useEffect(load, [load]);
  const t = data?.totals || {};
  return (
    <div className="space-y-4" data-testid="ai-usage-log">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h3 className="font-medium text-neutral-900">AI usage log</h3><p className="text-sm text-neutral-500">Entries are kept for {data?.retention_days || 90} days. Prompts and customer messages are not stored. Token counts are approximate.</p></div>
        <div className="flex gap-2">
          <select className={sel} value={feature} onChange={(e) => setFeature(e.target.value)} data-testid="ai-usage-filter-feature"><option value="">All features</option>{FEATURES.map((f) => <option key={f}>{f}</option>)}</select>
          <select className={sel} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="ai-usage-filter-status"><option value="">All statuses</option><option value="success">Success</option><option value="failed">Failed</option></select>
          <Button size="sm" variant="outline" onClick={load} data-testid="ai-usage-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat id="requests" label="Requests" value={fmt(t.requests)} /><Stat id="tokens" label="Tokens" value={fmt(t.tokens)} />
        <Stat id="quality" label="Quality model" value={fmt(t.quality)} /><Stat id="fallbacks" label="Fallbacks" value={fmt(t.fallbacks)} /><Stat id="failed" label="Failed" value={fmt(t.failed)} />
      </div>
      <div className="bg-white border border-neutral-200 rounded-lg overflow-x-auto">
        {!data ? <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div> : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-xs text-neutral-500 text-left"><tr><th className="px-3 py-2">Date / time</th><th className="px-3 py-2">Feature</th><th className="px-3 py-2">Selected model</th><th className="px-3 py-2">Answered by</th><th className="px-3 py-2">Routing</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Tokens</th></tr></thead>
            <tbody>{data.entries.length ? data.entries.map((r) => <Row key={r.id} r={r} />) : <tr><td colSpan={7} className="px-3 py-8 text-center text-neutral-400" data-testid="ai-usage-empty">No AI requests logged yet.</td></tr>}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};
